import React, { useState, useEffect } from 'react';
import { ReactComponent as FileIcon } from 'assets/icons/file-icon.svg';
import LocalSideModal from 'components/base/side-modal';
import {
  DateRangeSelector,
  ENUM_DATE_RANGE,
  ToastMessage,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import { NotificationType } from 'models/production-report.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { PANEL_ROLES } from 'utils/roles';
import { readStoredRole } from 'utils/authorization';
import { getSimpleDateFormat } from 'utils/functions';
import InfiniteScroll from 'react-infinite-scroll-component';
import './styles.scss';

type NotificationProps = {
  show: boolean;
  onClose: () => void;
};

const NotificationSideModal = ({ show, onClose }: NotificationProps) => {
  const [filters, setFilters] = useState({
    dateRange: getDateRangeSelector(ENUM_DATE_RANGE.THIS_MONTH)
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<NotificationType[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const role = (readStoredRole()?.name ?? '') as PANEL_ROLES;
  const { startDate: start_date, endDate: end_date } = filters.dateRange;

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`production-history/notification`, {
        params: {
          start_date,
          end_date,
          type: role,
          page: page
        }
      });
      setNotification(res?.data?.data?.results);
      setTotalPages(res?.data?.data?.total);
    } catch (err: any) {
      console.log(err?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [role, start_date, end_date]);

  useEffect(() => {
    if (page > 1) {
      fetchNotifications();
    }
  }, [page]);

  const fetchMoreData = () => {
    setPage((p) => p + 1);
  };

  const readNotifications = (notificationId: string) => {
    const data = {
      id: notificationId
    };
    const updatedNotificationData = notification?.map((item) =>
      item.id === notificationId ? { ...item, is_read: true } : item
    );
    setNotification(updatedNotificationData);
    try {
      apiService.put('production-history/notification', { data });
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    }
  };

  return (
    <div>
      <LocalSideModal
        isOpen={show}
        onRequestClose={onClose}
        title={
          <div className='flex justify-between gap-6'>
            <div>
              <h3 className='text-xl md:text-2xl text-[#2E3031] font-bold'>Notifications</h3>
              <h3 className='text-[#96999C] text-xs mt-3'>Latest updates</h3>
            </div>
            <DateRangeSelector
              defaultRange={ENUM_DATE_RANGE.THIS_MONTH}
              onChange={(v) => setFilters({ ...filters, dateRange: v.date })}
            />
          </div>
        }>
        <div className='my-4'>
          {loading ? (
            <div className=' text-sm text-center my-8'>Loading ...</div>
          ) : notification.length === 0 ? (
            <div className=' text-sm text-center my-8'>No notifications available</div>
          ) : (
            <InfiniteScroll
              className='infinite_scroll_div'
              loader={
                <p className='text-sm flex justify-center mt-8'>Loading more notifications...</p>
              }
              dataLength={notification?.length}
              hasMore={totalPages > (notification ? notification?.length : 0)}
              height={520}
              next={fetchMoreData}
              endMessage={<p className='text-sm text-center my-4'>Yay! You have seen it all</p>}>
              {notification.map((item) => (
                <div
                  key={item.id}
                  onClick={() => readNotifications(item.id)}
                  className='flex justify-between w-full items-center gap-3 py-3'>
                  <div className='flex gap-3'>
                    <div className='bg-[#F4F1F1] min-w-[28px] h-[26px] rounded-full flex justify-center items-center'>
                      <FileIcon />
                    </div>
                    <div className='flex flex-col gap-1'>
                      <p className='text-[#5E6164] text-sm'>{item.message}</p>
                      <p className='text-[#737373] text-[10px]'>
                        {getSimpleDateFormat(item.updated_at)}
                      </p>
                    </div>
                  </div>

                  <div
                    className={`min-w-[10px] h-[10px] ${
                      item.is_read ? 'bg-[#EDEDEE]' : 'bg-[#E60A2B]'
                    }  rounded-full `}></div>
                </div>
              ))}
            </InfiniteScroll>
          )}
        </div>
      </LocalSideModal>
    </div>
  );
};

export default NotificationSideModal;
